import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../api/axios';
import { useLoading } from '../../../components/LoadingProvider';
import useAuth from '../../../hooks/useAuth';
import { showSuccess, showError } from '../../../utils/toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import moment from 'moment';

export default function DoctorAppointmentDetailPage() {
    const navigate = useNavigate();
    const chatDivRef = useRef(null);
    const { setIsLoading } = useLoading();
    const MySwal = withReactContent(Swal);
    const { id } = useParams();
    const { user, isAuthenticated, userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['doctor'] });
    document.title = `Varosa - Doctor Appointment No ${id}`;
    const [accordionStatus, setAccordionStatus] = useState({
        one: true,
        two: true,
        three: true,
        four: true,
        five: true,
        six: true,
    });
    const [ appointmentData, setAppointmentData ] = useState(null);
    const [ availableSlots, setAvailableSlots ] = useState({});
    const [ selectedDoctorDate, setSelectedDoctorDate ] = useState('');
    const [ selectedSlot, setSelectedSlot ] = useState(null);
    const [ chatMessages, setChatMessages ] = useState([]);
    const [ appointmentStatus, setAppointmentStatus ] = useState('upcoming');
    const [ unseenMessagesCount, setUnseenMessagesCount ] = useState(0);
    const [ videoCallLink, setVideoCallLink ] = useState(null);
    /* window.Echo.channel('appointment-chat.' + id + '.' + user?.id)
        .listen('AppointmentMessageSent', (e) => {
            setChatMessages(prevMessages => [...prevMessages, e.message]);
            if (!e.message.seen) {
                setUnseenMessagesCount(prevCount => prevCount + 1);
            }
        }); */
    const [chatFormData, setChatFormData] = useState({
        message: '',
    });
    const [chatFormError, setChatFormError] = useState({
        message: '',
    });
    useEffect(() => {
        fetchAppointment();
        fetchChatMessages();

        const updateDoctorStatus = async (status) => {
            try {
                await api.post(`/doctor/appointment/${id}/status/${status}`);
            } catch (error) {
                console.error(`Error updating doctor status to ${status}:`, error);
            }
        };

        updateDoctorStatus('online');

        const timeInterval = 3500;
        const interval = setInterval(() => fetchChatMessages(), timeInterval);
        
        const handleBeforeUnload = () => {
            const url = `${api.defaults.baseURL}/doctor/appointment/${id}/status/offline`;
            const token = localStorage.getItem('token');
            const blob = new Blob([], { type: 'application/json' });
            // Using navigator.sendBeacon for reliable delivery on page close
            // However, sendBeacon doesn't support headers, so we might need an alternative 
            // if authentication is strictly via Bearer token in headers.
            // For now, we'll try a standard fetch with keepalive.
            fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                keepalive: true
            });
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            clearInterval(interval);
            updateDoctorStatus('offline');
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [id]);
    const fetchAppointment = async () => {
        try {
            setIsLoading(true);
            const response = await api.get(`/${userRole}/appointment/${id}`);
            if (response?.data?.result === 'success') {
                setAppointmentData(response?.data?.data);
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching appointments:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const fetchAvailableSlots = async () => {
        if(accordionStatus.six){
            setAccordionStatus({ ...accordionStatus, six: !accordionStatus.six });
            setSelectedDoctorDate('');
            setSelectedSlot(null);
            return;
        }
        try {
            setIsLoading(true);
            const response = await api.get(`/doctor/available-slots`);
            if (response?.data?.result === 'success') {
                setAvailableSlots(response?.data?.data?.slots || {});
                if(Object.keys(response?.data?.data?.slots || {}).length > 0){
                    for (let key of Object.keys(response?.data?.data?.slots || {})) {
                        setSelectedDoctorDate(key);
                        break;
                    }
                }
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching available slots:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
            setAccordionStatus({ ...accordionStatus, six: !accordionStatus.six });
        }
    }
    const updateCanChat = (newCanChatValue) => {
        setAppointmentData(prevData => {
            // If appointment doesn't exist, return state as is
            if (!prevData?.appointment) {
                return prevData;
            }

            return {
                ...prevData,
                appointment: {
                    ...prevData.appointment,
                    can_chat: newCanChatValue
                }
            };
        });
    };
    const fetchChatMessages = async () => {
        try {
            // setIsLoading(true);
            const response = await api.get(`/appointment/${id}/chat`);
            if (response?.data?.result === 'success') {
                if(response?.data?.data?.length > 0){
                    updateCanChat(response?.data?.data[0].can_chat || false);
                }
                setChatMessages(response?.data?.data);
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error fetching chat messages:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const openCancelAppointmentPopop = (id) => {
        MySwal.fire({
            title: 'Are you sure?',
            text: 'You want to cancel the appointment?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel it!',
            cancelButtonText: 'No, keep it',
        }).then((result) => {
            if (result.isConfirmed) {
                handleCancelAppointment(id);
            }
        });
    }
    const renderReport = (report) => {
        switch(report.mime_type){
            case 'image/jpeg':
            case 'image/png':
            case 'image/jpg':
                return <><img src={report.url} alt={report.original_name} className="img-fluid" style={{ width: '100px' }} /> <span className="float-end">
                    <i className="fas fa-eye text-primary me-3" style={{cursor: 'pointer'}} onClick={() => viewFileViewFinder(report)}></i>
                </span></>;
            case 'application/pdf':
                return <>{report.original_name} <span className="float-end">
                    <i className="fas fa-eye text-primary me-3" style={{cursor: 'pointer'}} onClick={() => viewFileViewFinder(report)}></i>
                </span></>;
            default:
                return <>{report.original_name} <span className="float-end">
                    <i className="fas fa-eye text-primary me-3" style={{cursor: 'pointer'}} onClick={() => viewFileViewFinder(report)}></i>
                </span></>;
        }
    }
    const viewFileViewFinder = async (report) => {
        MySwal.fire({
            title: 'File View Finder: ' + report.original_name,
            html: `<iframe src="${report.url}" alt="File View Finder: ${report.original_name}" style="width: 100%;height: 600px;"></iframe>`,
            width: '90%',
            showConfirmButton: true,
            confirmButtonText: 'Close',
            customClass: {
                container: '',
            },
        });
    };
    const handleCancelAppointment = async (appointmentId) => {
        try{
            const response = await api.post(`/appointment/${appointmentId}/cancel`);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        }catch(error){
            showError('Error canceling appointment:', error?.response?.data?.message || error.message);
        }
    }
    const sendChatMessage = async () => {
        try {
            if(!chatFormData?.message){
                setChatFormError({
                    ...chatFormError,
                    message: 'Please enter a message',
                });
                return;
            }
            setChatFormError({
                ...chatFormError,
                message: '',
            });
            setIsLoading(true);
            const response = await api.post(`/appointment/${id}/chat`, {
                message: chatFormData?.message,
            });
            if (response?.data?.result === 'success') {
                setChatFormData({
                    ...chatFormData,
                    message: '',
                });
                setChatMessages(prevMessages => [...prevMessages, response?.data?.data]);
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error sending chat message:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const savePrescriptionData = async () => {
        try {
            setIsLoading(true);
            const response = await api.post(`/${userRole}/appointment/${id}/update-prescription`, {
                clinical_summary: appointmentData?.appointment?.clinical_summary,
                prescribe_meds: appointmentData?.appointment?.prescribe_meds,
                lab_test_suggestion: appointmentData?.appointment?.lab_test_suggestion,
            });
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error saving prescription data:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }
    const scrollToChatDiv = () => {
      if (chatDivRef.current) {
        chatDivRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setAccordionStatus({ ...accordionStatus, four: true })
      }
    }
    const viewReport = async (report, isItUrl = false) => {
        try{
            if(isItUrl){
                MySwal.fire({
                    title: 'Report',
                    html: `<iframe src="${report}" alt="Report" style="width: 100%;height: 600px;"></iframe>`,
                    width: '90%',
                    showConfirmButton: true,
                    confirmButtonText: 'Close',
                    customClass: {
                        container: '',
                    },
                });
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                const base64String = reader.result;
                if(base64String){
                    MySwal.fire({
                        title: 'Report',
                        html: `<iframe src="${base64String}" alt="Report" style="width: 100%;height: 600px;"></iframe>`,
                        width: '90%',
                        showConfirmButton: true,
                        confirmButtonText: 'Close',
                        customClass: {
                            container: '',
                        },
                    });
                }
            };
            reader.readAsDataURL(report);
        }catch(error){
            showError('Something went wrong! Please try again later.');
            return;
        }
    };

    const openRescheduleAppointmentPopop = (id) => {
        // if(!selectedSlot){
        //     showError('Please select a slot!');
        //     return;
        // }
        // MySwal.fire({
        //     title: 'Are you sure?',
        //     text: 'You want to reschedule the appointment?',
        //     icon: 'warning',
        //     showCancelButton: true,
        //     confirmButtonText: 'Yes, reschedule it!',
        //     cancelButtonText: 'No, keep it',
        // }).then((result) => {
        //     if (result.isConfirmed) {
        //         handleRescheduleAppointment(id);
        //     }
        // });
    }
    const handleRescheduleAppointment = async (appointmentId) => {
        try{
            // if(!selectedSlot){
            //     showError('Please select a slot!');
            //     return;
            // }
            // setIsLoading(true);
            // const response = await api.post(`/${userRole}/appointment/${appointmentId}/reschedule`, {
            //     appointment_id: appointmentId,
            //     updated_slot_id: selectedSlot?.id,
            // });
            // if (response?.data?.result === 'success') {
            //     showSuccess(response?.data?.message);
            //     window.location.reload();
            //     return;
            // } else if (response?.data?.result === 'failed') {
            //     showError(response?.data?.message);
            //     if(response?.data?.errors){
            //         response?.data?.errors.forEach((item, index, arr) => {
            //             showError(item);
            //         });
            //     }
            // } else {
            //     showError('Something went wrong! Please try again later.');
            // }
        } catch(error) {
            showError('Error canceling appointment:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }

    const markAsAppointmentCompleted = () => {
        MySwal.fire({
            title: 'Are you sure?',
            text: 'You want to complete this Appointment?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, start it!',
            cancelButtonText: 'No, keep it',
        }).then(async (result) => {
            try{
                setIsLoading(true);
                const response = await api.post(`/${userRole}/appointment/${id}/mark_as_completed`, {
                    is_completed: result.isConfirmed ? 'Yes' : 'No'
                });
                if (response?.data?.result === 'success') {
                    showSuccess(response?.data?.message);
                    window.location.reload();
                    return;
                } else if (response?.data?.result === 'failed') {
                    showError(response?.data?.message);
                    if(response?.data?.errors){
                        response?.data?.errors.forEach((item, index, arr) => {
                            showError(item);
                        });
                    }
                } else {
                    showError('Something went wrong! Please try again later.');
                }
            } catch(error) {
                showError('Error starting video call:', error?.response?.data?.message || error.message);
            } finally {
                setIsLoading(false);
            }
        });
    }

    const startVideoCall = () => {
        MySwal.fire({
            title: 'Are you sure?',
            text: 'You want to start the video call?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, start it!',
            cancelButtonText: 'No, keep it',
        }).then((result) => {
            if (result.isConfirmed) {
                handleStartVideoCall();
            }
        });
    }
    const renderPrescriptionDetails = () => {
        const prescription = appointmentData?.appointment_prescription || {};
        switch (prescription?.prescription_type) {
            case 'physiotherapy':
                return (
                    <>
                        <div className="mb-3">
                            <label className="form-label">CHIEF COMPLAINTS</label>
                            <textarea className="form-control" value={prescription?.chief_complaints} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">HISTORY OF PRESENT ILLNESS (HOPI)</label>
                            <textarea className="form-control" value={prescription?.hopi} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">EXAMINATION</label>
                            <textarea className="form-control" value={prescription?.examination} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">DIAGNOSIS</label>
                            <textarea className="form-control" value={prescription?.diagnosis} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">MANAGEMENT</label>
                            <textarea className="form-control" value={prescription?.management} readOnly></textarea>
                        </div>
                    </>
                );
            case 'speech_therapy':
                return (
                    <>
                        <div className="mb-3">
                            <label className="form-label">PROVISIONAL DIAGNOSIS</label>
                            <textarea className="form-control" value={prescription?.provisional_diagnosis} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">PRESENT CONCERNS</label>
                            <textarea className="form-control" value={prescription?.present_concerns} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">ASSESSMENT FINDINGS</label>
                            <textarea className="form-control" value={prescription?.assessment_findings} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">ADVICE / HOME PROGRAM</label>
                            <textarea className="form-control" value={prescription?.advice_home_program} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">RECOMMENDATIONS</label>
                            <textarea className="form-control" value={prescription?.recommendations} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">FREQUENCY OF THERAPY</label>
                            <input type="text" className="form-control" value={prescription?.frequency_of_therapy || ''} readOnly />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">REVIEW DATE</label>
                            <input type="text" className="form-control" value={prescription?.review_date || ''} readOnly />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">THERAPY PLAN</label>
                            <ol>
                            {prescription?.therapy_plan?.map((item, index) => (
                                <li key={item.id || index} style={{border: '1px solid #ced4da', backgroundColor: '#e9ecef', padding: '.375rem .75rem', marginBottom: '10px', borderRadius: '10px'}}>
                                    {item.instruction}
                                </li>
                            ))}
                            </ol>
                        </div>
                    </>
                );
            case 'diet_prescription':
                return (
                    <>
                        <div className="mb-3">
                            <label className="form-label">CEREALS</label>
                            <input type="text" className="form-control" value={(prescription?.cereals || []).join(', ')} readOnly />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">DAL AND DAL PRODUCTS</label>
                            <textarea className="form-control" value={prescription?.dal_and_dal_products} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">GREEN LEAFY VEGETABLES</label>
                            <textarea className="form-control" value={prescription?.green_leafy_vegetables} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">OTHER VEGETABLES</label>
                            <textarea className="form-control" value={prescription?.other_vegetables} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">FRUITS</label>
                            <input type="text" className="form-control" value={prescription?.fruits || ''} readOnly />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">FISH</label>
                            <input type="text" className="form-control" value={prescription?.fish || ''} readOnly />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">CHICKEN</label>
                            <input type="text" className="form-control" value={prescription?.chicken || ''} readOnly />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">EGG</label>
                            <input type="text" className="form-control" value={prescription?.egg || ''} readOnly />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">OIL</label>
                            <input type="text" className="form-control" value={prescription?.oil || ''} readOnly />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">MILK</label>
                            <input type="text" className="form-control" value={prescription?.milk || ''} readOnly />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">FOODS TO AVOID</label>
                            <ul>
                            {prescription?.foods_to_avoid?.filter((item) => item?.checked !== false).map((item, index) => (
                                <li key={item.id || index}>{item.item}</li>
                            ))}
                            </ul>
                        </div>
                    </>
                );
            default:
                return (
                    <>
                        <div className="mb-3">
                            <label className="form-label">CHIEF COMPLAINTS</label>
                            <textarea className="form-control" value={prescription?.chief_complaints} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">RELEVANT POINTS FROM HISTORY</label>
                            <textarea className="form-control" value={prescription?.history_points} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">EXAMINATION/LAB FINDINGS</label>
                            <textarea className="form-control" value={prescription?.lab_findings} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">SUGGESTED INVESTIGATIONS</label>
                            <textarea className="form-control" value={prescription?.suggested_investigations} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">SPECIAL INSTRUCTIONS</label>
                            <textarea className="form-control" value={prescription?.special_instructions} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">DIAGNOSIS</label>
                            <textarea className="form-control" value={prescription?.diagnosis} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">REMARKS</label>
                            <textarea className="form-control" value={prescription?.remarks} readOnly></textarea>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">RX</label>
                            <ol>
                            {prescription?.med_rx_products?.map((item, index) => (
                                <li key={index} style={{border: '1px solid #ced4da', backgroundColor: '#e9ecef', padding: '.375rem .75rem', marginBottom: '10px', borderRadius: '10px'}}>
                                    <div className="mb-3">
                                        <label className="form-label">{item.product}</label>
                                        <p className="mb-0">
                                            <span className="fw-bold">Dosage:</span> {item.dosage}
                                        </p>
                                        <p className="mb-0">
                                            <span className="fw-bold">Route:</span> {item.route}
                                        </p>
                                        <p className="mb-0">
                                            <span className="fw-bold">Frequency:</span> {item.frequency}
                                        </p>
                                        <p className="mb-0">
                                            <span className="fw-bold">Schedule:</span> {item.schedule}
                                        </p>
                                        <p className="mb-0">
                                            <span className="fw-bold">Instruction:</span> {item.instruction}
                                        </p>
                                        <p className="mb-0">
                                            <span className="fw-bold">Duration:</span> {item.duration}
                                        </p>
                                    </div>
                                </li>
                            ))}
                            </ol>
                        </div>
                    </>
                );
        }
    }
    const handleStartVideoCall = async () => {
        try{
            setIsLoading(true);
            const response = await api.post(`/${userRole}/appointment/${id}/start_video_call`);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                setVideoCallLink(response?.data?.data?.video_call_link);
                window.open(response?.data?.data?.video_call_link, "_blank");
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch(error) {
            showError('Error starting video call:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            {appointmentData?.appointment?.payment_status === 'paid' &&
            (<><button className="video-call-btn" type="button" onClick={startVideoCall}><i className="fas fa-video"></i> Start Video Call</button>
            <button className="start-chat-btn" type="button" onClick={scrollToChatDiv}><i className="fas fa-comments"></i> Talk To Patient</button></>)}
            <div className="px-5 py-5 login-container appointment-detail">
                <div className="row g-3">
                    <div className="col-8">
                        <h2 className="mb-4">Appointment No {id} (Booked at {appointmentData?.appointment?.appointment_date_time})</h2>
                        <small className="text-muted mb-3">Status: {appointmentData?.appointment?.booking_status}</small>
                    </div>
                    <div className="col-4 text-end">
                        <button onClick={() => window.history.go(-1)} className="btn btn-primary">Back</button>
                    </div>
                    <hr />
                </div>
                <div className="row g-3">
                    <div className="col-md-6">
                        <div className="accordion">
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingOne" 
                                    onClick={() => setAccordionStatus({ ...accordionStatus, one: !accordionStatus.one })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.one ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseOne" aria-expanded="true" aria-controls="panelsStayOpen-collapseOne">
                                        Patient Details
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseOne" className={`accordion-collapse collapse ${accordionStatus.one ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingOne">
                                    <div className="accordion-body">
                                        <div className="row">
                                            <div className="col-12">
                                                <h5 className="mb-3 mt-2">Name: {appointmentData?.appointment?.patient_name}</h5>
                                                <p className="mb-3">Date of Birth: {appointmentData?.appointment?.patient_dob}</p>
                                                <p className="mb-3">Gender: {appointmentData?.appointment?.patient_gender}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {appointmentData?.appointment?.payment_status === 'paid' && 
                        (<div className="accordion mt-3">
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingThree"
                                    onClick={() => setAccordionStatus({ ...accordionStatus, three: !accordionStatus.three })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.three ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseThree" aria-expanded="false" aria-controls="panelsStayOpen-collapseThree">
                                        Patient Prescription
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseThree" className={`accordion-collapse collapse ${accordionStatus.three ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingThree">
                                    <div className="accordion-body">
                                        <form>
                                            {appointmentData?.appointment_prescription?.prescription_type_label && (
                                                <div className="mb-3">
                                                    <span className="badge bg-primary">{appointmentData?.appointment_prescription?.prescription_type_label}</span>
                                                </div>
                                            )}
                                            {renderPrescriptionDetails()}
                                            {appointmentData?.appointment?.doctor_invoice_url && (
                                                <button onClick={() => { window.open(appointmentData?.appointment?.doctor_invoice_url, '_blank'); }} className="btn btn-primary m-2">View Invoice</button>
                                            )}
                                            {appointmentData?.doctor?.prescription_types?.length > 0 ? (
                                                <div className="btn-group m-2">
                                                    {appointmentData?.doctor?.prescription_types?.map((item) => (
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-primary m-2" 
                                                            onClick={() => navigate(`/dashboard/doctor/appointment-prescription/${item.value}/${appointmentData?.appointment?.id}`)}
                                                        >Generate {item.label}</button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <button type="button" className="btn btn-primary m-2" onClick={() => navigate(`/dashboard/doctor/appointment-prescription/${appointmentData?.appointment?.id}`)}>Generate Prescription</button>
                                            )}
                                            <button type="button" className="btn btn-success m-2" onClick={markAsAppointmentCompleted}>Mark as Appointment Complete</button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>)}
                        <div className="accordion mt-3">
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingFive"
                                    onClick={() => setAccordionStatus({ ...accordionStatus, five: !accordionStatus.five })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.five ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseFive" aria-expanded="false" aria-controls="panelsStayOpen-collapseFive">
                                        Payment Status
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseFive" className={`accordion-collapse collapse ${accordionStatus.five ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingFive">
                                    <div className="accordion-body">
                                        <p className="mb-3">Payment Status: {appointmentData?.appointment?.payment_status}</p>
                                        <p className="mb-3">Payment Amount: <b>&#8377;{appointmentData?.appointment?.paid_amount}</b></p>
                                        {appointmentData?.appointment?.prescription_url && (
                                            <button onClick={() => { window.open(appointmentData?.appointment?.prescription_url, '_blank'); }} className="btn btn-primary mt-1">Print Prescription</button>
                                        )}
                                        {appointmentData?.appointment?.can_cancel && (
                                            <button onClick={() => { openCancelAppointmentPopop(appointmentData?.appointment?.id); }} className="btn btn-primary mt-1">Cancel Appointment</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {appointmentData?.appointment?.can_reschedule && (
                        <div className="accordion mt-3 d-none" stye={{ display: 'none'}}>
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingSix"
                                    onClick={() => fetchAvailableSlots()}
                                >
                                    <button className={`accordion-button ${!accordionStatus.six ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseSix" aria-expanded="false" aria-controls="panelsStayOpen-collapseSix">
                                        Reschedule Appointment
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseSix" className={`accordion-collapse collapse ${accordionStatus.six ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingSix">
                                    <div className="accordion-body">
                                        {Object.keys(availableSlots).length === 0 && <p className="text-danger">No available slots</p>}
                                        {Object.keys(availableSlots).length > 0 &&
                                            <>
                                                <p><b>Please choose date to view available time slots</b></p>
                                                <ul className="nav nav-pills doctor-time-slot">
                                                    {Object.entries(availableSlots).map(([key, value]) => (
                                                        <li key={key} className="nav-item m-1">
                                                            <button className={`nav-link nav-tab-link-${appointmentData?.doctor?.id}-${key} ${selectedDoctorDate === key ? 'active' : ''}`} onClick={() => { setSelectedDoctorDate(key); }}>
                                                                {moment(key).format('ddd, D MMM YYYY')}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                                <hr />
                                                {Object.entries(availableSlots).map(([key, value]) => (
                                                    <div key={key} className={`nav-body nab-tab-body-${appointmentData?.doctor?.id}-${key} ${selectedDoctorDate === key ? 'd-block' : 'd-none'}`}>
                                                        {
                                                            value.map((time) => (
                                                                <span key={time.id} className="m-2">
                                                                    <input type="radio" className="btn-check" name={`options-outlined-${appointmentData?.doctor?.id}-${time.id}`} id={`selected-slot-${appointmentData?.doctor?.id}-${time.id}`} checked={selectedSlot?.id === time?.id} onChange={() => { setSelectedSlot({ ...time }); }} />
                                                                    <label className="btn btn-outline-success" htmlFor={`selected-slot-${appointmentData?.doctor?.id}-${time.id}`}>{time.start_datetime}</label>
                                                                </span>
                                                            ))
                                                        }
                                                    </div>)
                                                )}
                                                <hr />
                                                <button onClick={() => { openRescheduleAppointmentPopop(appointmentData?.appointment?.id); }} className="btn btn-primary">Reschedule Appointment</button>
                                            </>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}
                        <div className="accordion mt-3 d-none">
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingTwo"
                                    onClick={() => setAccordionStatus({ ...accordionStatus, two: !accordionStatus.two })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.two ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseTwo" aria-expanded="false" aria-controls="panelsStayOpen-collapseTwo">
                                        Patient Medical Details
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseTwo" className={`accordion-collapse collapse ${accordionStatus.two ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingTwo">
                                    <div className="accordion-body">
                                        <p className="mb-3">Patient Problem: {appointmentData?.appointment?.visit_reason}</p>
                                        <p className="mb-3">Patient Name: {appointmentData?.appointment?.patient_name}</p>
                                        <p className="mb-3">Patient Age: {appointmentData?.appointment?.patient_dob}</p>
                                        <p className="mb-3">Patient Gender: {appointmentData?.appointment?.patient_gender}</p>
                                        <p className="mb-3">Patient Contact: {appointmentData?.appointment?.patient_phone_numbers}</p>
                                        <p className="mb-3">Patient Email: {appointmentData?.appointment?.patient_email}</p>
                                        <p className="mb-3">Patient Address: {appointmentData?.appointment?.patient_address_line1}</p>
                                        <p className="mb-3">Patient Address Line 2: {appointmentData?.appointment?.patient_address_line2}</p>
                                        <p className="mb-3">Patient District: {appointmentData?.appointment?.patient_district}</p>
                                        <p className="mb-3">Patient State: {appointmentData?.appointment?.patient_state}</p>
                                        <p className="mb-3">Patient Pincode: {appointmentData?.appointment?.patient_pincode}</p>
                                        <p className="mb-3">Patient Country: {appointmentData?.appointment?.patient_country}</p>
                                        <p className="mb-3">Patient Allergies: {appointmentData?.appointment?.patient_allergies}</p>
                                        <p className="mb-3">Patient Regular Medicines: {appointmentData?.appointment?.patient_regular_medicines}</p>
                                        <p className="mb-3">Patient Reports: {appointmentData?.appointment?.patient_reports?.map((report) => (
                                            <button key={report} onClick={() => viewReport(report)} className="btn btn-primary me-2">View Report</button>
                                        ))}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="accordion d-none">
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingSix"
                                    onClick={() => setAccordionStatus({ ...accordionStatus, six: !accordionStatus.six })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.six ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseSix" aria-expanded="false" aria-controls="panelsStayOpen-collapseSix">
                                        Video Call with patient
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseSix" className={`accordion-collapse collapse ${accordionStatus.six ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingSix">
                                    <div className="accordion-body w-100" style={{ minHeight: '500px' }}>
                                        {videoCallLink ? (
                                            <iframe src={videoCallLink} className="w-100" style={{ minHeight: '450px' }} title="Video Call with patient" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen="true"></iframe>
                                        ) : (
                                            <div className="row">
                                                <div className="col-12">
                                                    <div className="bg-light rounded p-4">
                                                        <h2 className="mb-0">Video call link not available</h2>
                                                        <button className="btn btn-primary mt-3" type="button" onClick={startVideoCall}><i className="fas fa-video"></i> Start Video Call</button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="accordion" ref={chatDivRef}>
                            <div className="accordion-item">
                                <h2 
                                    className={`accordion-header`} 
                                    id="panelsStayOpen-headingFive"
                                    onClick={() => setAccordionStatus({ ...accordionStatus, five: !accordionStatus.five })}
                                >
                                    <button className={`accordion-button ${!accordionStatus.five ? 'collapsed' : ''}`} type="button" data-bs-toggle="collapse" data-bs-target="#panelsStayOpen-collapseFour" aria-expanded="false" aria-controls="panelsStayOpen-collapseFour">
                                        Chat with patient
                                    </button>
                                </h2>
                                <div id="panelsStayOpen-collapseFive" className={`accordion-collapse collapse ${accordionStatus.five ? 'show' : ''}`} aria-labelledby="panelsStayOpen-headingFive">
                                    <div className="accordion-body">
                                        <ul className="list-unstyled mb-0">
                                            {chatMessages?.map((message, index, arr) => {
                                                return message.me ? (
                                                <li key={index} className="d-flex mb-4">
                                                    <span className="rounded-circle d-flex align-self-start me-3 shadow-1-strong mt-4">
                                                        <i className="fas fa-user-md fa-lg" style={{ color: '#000' }}></i>
                                                    </span>
                                                    <div className="card w-100">
                                                        <div className="card-header d-flex justify-content-between p-3">
                                                            <p className="fw-bold mb-0">{message?.user_name}</p>
                                                            <p className="text-muted small mb-0">{message?.created_at}</p>
                                                        </div>
                                                        <div className="card-body">
                                                            {message?.message && (
                                                                <p className="mb-0">{message?.message}</p>
                                                            )}
                                                            {message?.files.length > 0 && (<>
                                                                <hr />
                                                                <div className="mt-1 mb-1">
                                                                    <ul className="list-group">
                                                                        {message?.files.map((file, index) => (
                                                                        <li key={index} className="list-group-item">
                                                                            {renderReport(file)}
                                                                        </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            </>)}
                                                        </div>
                                                        {message?.link && <div className="card-footer text-end" dangerouslySetInnerHTML={{ __html: message?.link }}></div>}
                                                    </div>
                                                </li>
                                                ):(
                                                <li key={index} className="d-flex mb-4">
                                                    <div className="card w-100">
                                                        <div className="card-header d-flex justify-content-between p-3">
                                                            <p className="fw-bold mb-0">{message?.user_name}</p>
                                                            <p className="text-muted small mb-0">{message?.created_at}</p>
                                                        </div>
                                                        <div className="card-body">
                                                            {message?.message && (
                                                                <p className="mb-0">{message?.message}</p>
                                                            )}
                                                            {message?.files.length > 0 && (<>
                                                                <hr />
                                                                <div className="mt-1 mb-1">
                                                                    <ul className="list-group">
                                                                        {message?.files.map((file, index) => (
                                                                        <li key={index} className="list-group-item">
                                                                            {renderReport(file)}
                                                                        </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            </>)}
                                                        </div>
                                                    </div>
                                                    <span className="rounded-circle d-flex align-self-start ms-3 shadow-1-strong mt-4">
                                                        <i className="fas fa-user fa-lg" style={{ color: '#000' }}></i>
                                                    </span>
                                                </li>
                                                )
                                            })}
                                            <li className="bg-white">
                                                {appointmentData?.appointment?.can_chat ? (
                                                <><hr />
                                                <div className="form-outline">
                                                    <label className="form-label" htmlFor="message">Message</label>
                                                    <textarea 
                                                        className="form-control bg-body-tertiary" 
                                                        rows="4" 
                                                        name="message" 
                                                        id="message" 
                                                        value={chatFormData.message}
                                                        onChange={(e) => setChatFormData({ ...chatFormData, message: e.target.value })}
                                                    ></textarea>
                                                    {chatFormError.message && <div className="invalid-feedback d-block">Please enter a message.</div>}
                                                    <div className="text-end">
                                                        <button type="button" className="btn btn-info btn-rounded mt-3" onClick={sendChatMessage}>Send</button>
                                                    </div>
                                                </div></>
                                                ) : (<p className="text-muted text-center">Chat is Disabled</p>)}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
